/**
 * The home's CTA, carried to every page with the header and footer (SCRUM-2631).
 *
 * A visitor drawn into an "Assine agora" version of the home (home-v2, home-v4) must see "Assine agora", never the free
 * trial, in the header and footer of every page while the assignment lasts. The api's assignment cookie
 * (`noping_home_page_assignment`) is encrypted and opaque to the site, and only the home asks the api, so the home also
 * writes this readable cookie with the same lifetime: `noping_home_cta=assine`. It holds no personal data and nothing
 * the visitor could abuse: it only picks between two CTAs the site already renders.
 *
 * Most pages are static, so the server cannot read the cookie for them. The header and footer render both CTAs, each
 * marked with `data-home-cta-slot`, and a tiny inline script in `_document` (HOME_CTA_INLINE_SCRIPT) marks
 * `<html data-home-cta="assine">` before the first paint; the inline rules in HOME_CTA_STYLE hide the CTA that does not
 * apply. No flash of "Teste grátis", no hydration mismatch (React renders the same markup on the server and the client),
 * and the static pages stay static. `_app` calls syncHomeCtaAttribute after every client-side navigation, because the
 * home's `/_next/data` answer may have set or cleared the cookie.
 */

export const HOME_CTA_COOKIE = 'noping_home_cta';
export const HOME_CTA_ASSINE = 'assine';
/** Attribute on `<html>`: present (`assine`) while the visitor is in an "Assine agora" version. */
export const HOME_CTA_DOCUMENT_ATTRIBUTE = 'data-home-cta';
/** Attribute on each CTA the header and footer render twice: `teste` (free trial) or `assine` (Assine agora). */
export const HOME_CTA_SLOT_ATTRIBUTE = 'data-home-cta-slot';
export type HomeCtaSlot = 'teste' | 'assine';

/** Where "Assine agora" leads outside the home: Planos e preços, as on the home itself. */
export const HOME_CTA_ASSINE_HREF = '/pricing';

/** The api keeps the visitor on the same version for 24h; used only when its cookie gives no lifetime (E2E override). */
const DEFAULT_MAX_AGE_SECONDS = 24 * 60 * 60;

/**
 * `Set-Cookie` that pins "Assine agora" for as long as the api's assignment cookie lives: its `Max-Age`/`Expires` and
 * `Secure` are copied, so both cookies expire together. Not HttpOnly on purpose: the inline script reads it.
 */
export function homeCtaAssineSetCookie(apiSetCookie: string | null): string {
  const attributes = (apiSetCookie ?? '').split(';').map((part) => part.trim());
  const lifetime = attributes.filter((attribute) => /^(max-age|expires)=/i.test(attribute));
  const secure = attributes.some((attribute) => attribute.toLowerCase() === 'secure');
  return [`${HOME_CTA_COOKIE}=${HOME_CTA_ASSINE}`, 'Path=/', ...(lifetime.length ? lifetime : [`Max-Age=${DEFAULT_MAX_AGE_SECONDS}`]), 'SameSite=Lax', ...(secure ? ['Secure'] : [])].join('; ');
}

/** `Set-Cookie` that drops the pin: the visitor's version offers the free trial. */
export function homeCtaClearSetCookie(): string {
  return `${HOME_CTA_COOKIE}=; Path=/; Max-Age=0; SameSite=Lax`;
}

/** Whether a `document.cookie`-style string pins "Assine agora". */
export function isHomeCtaAssine(cookieString: string): boolean {
  return cookieString.split(';').some((part) => part.trim() === `${HOME_CTA_COOKIE}=${HOME_CTA_ASSINE}`);
}

/** Marks or unmarks `<html>` from the current cookie. Client only. */
export function syncHomeCtaAttribute(): void {
  if (typeof document === 'undefined') return;
  if (isHomeCtaAssine(document.cookie)) document.documentElement.setAttribute(HOME_CTA_DOCUMENT_ATTRIBUTE, HOME_CTA_ASSINE);
  else document.documentElement.removeAttribute(HOME_CTA_DOCUMENT_ATTRIBUTE);
}

/**
 * Runs in `<head>`, before the body is parsed: the same check as syncHomeCtaAttribute, written out because it cannot import.
 * The Jest suite runs this exact string against both cookie states, so it cannot drift from isHomeCtaAssine.
 */
export const HOME_CTA_INLINE_SCRIPT = `try{if(document.cookie.split(';').some(function(c){return c.trim()==='${HOME_CTA_COOKIE}=${HOME_CTA_ASSINE}'}))document.documentElement.setAttribute('${HOME_CTA_DOCUMENT_ATTRIBUTE}','${HOME_CTA_ASSINE}')}catch(e){}`;

/** Inline in `<head>` so it holds even before the stylesheet arrives. `!important` beats the CTAs' own display classes. */
export const HOME_CTA_STYLE = `html[${HOME_CTA_DOCUMENT_ATTRIBUTE}="${HOME_CTA_ASSINE}"] [${HOME_CTA_SLOT_ATTRIBUTE}="teste"],html:not([${HOME_CTA_DOCUMENT_ATTRIBUTE}="${HOME_CTA_ASSINE}"]) [${HOME_CTA_SLOT_ATTRIBUTE}="assine"]{display:none!important}`;

/** Spread on a CTA rendered for one group only. */
export function homeCtaSlot(slot: HomeCtaSlot): { [HOME_CTA_SLOT_ATTRIBUTE]: HomeCtaSlot } {
  return { [HOME_CTA_SLOT_ATTRIBUTE]: slot };
}
